import { MenuItemText } from '@statsbomb/kitbag-components';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTrackExperienceChange } from '@/analytics/snowplow/hooks/useTrackExperienceChange';
import { ViewConfigurationSource, ViewType } from '@/analytics/snowplow/types';
import { patchUserNavOverridesAtom } from '@/atoms/mutations/user/user';
import { unwrappedUserNavOverridesAtom } from '@/atoms/queries/user';
import { toastDataAtom } from '@/atoms/toast';
import { useTeamNavItems } from '@/components/navigation/subNav/TeamNav';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import { useTeamNavigationItems } from './subNavigation/useTeamNavigationItems';
import { useTeamNavigationClassic, useTeamNavigationNew } from './useTeamNavigation';

export const useTeamNavigationItem = () => {
  const { t } = useTranslation('navigation');
  const isTouch = useIsTouchDevice();
  const navigate = useNavigate();
  const setToastData = useSetAtom(toastDataAtom);
  const { mutateAsync: patchUserNavOverrides } = useAtomValue(patchUserNavOverridesAtom);

  const userNavOverrides = useAtomValue(unwrappedUserNavOverridesAtom);
  const isClassicTeamXpEnabled = userNavOverrides.team;

  const newXpPath = useTeamNavigationNew();
  const newXpTeamSubnav = useTeamNavItems(newXpPath);
  const oldXpTeamSubnav = useTeamNavigationItems();

  const newXpDefaultPath = `${newXpPath}dashboard`;
  const oldXpDefaultPath = `${useTeamNavigationClassic()}dashboard`;

  const defaultTeamPath = isClassicTeamXpEnabled ? oldXpDefaultPath : newXpDefaultPath;
  const otherExperiencePath = isClassicTeamXpEnabled ? newXpDefaultPath : oldXpDefaultPath;

  const teamSubnav = isClassicTeamXpEnabled ? oldXpTeamSubnav : newXpTeamSubnav;
  const translationKey = isClassicTeamXpEnabled ? 'newExperience' : 'classicExperience';
  const trackExperienceChange = useTrackExperienceChange(ViewType.TEAM, ViewConfigurationSource.SIDE_NAVIGATION);

  const toggleTeamSectionXp = async () => {
    const isSaved = await patchUserNavOverrides({
      ...userNavOverrides,
      team: !isClassicTeamXpEnabled,
    })
      .then(({ isSuccess }) => isSuccess)
      .catch(() => false);
    if (!isSaved) {
      setToastData({ toastName: 'switchExperience', type: 'error' });
      return;
    }
    // Tracking is best-effort: a failed or slow analytics call must not block the redirect.
    trackExperienceChange().catch(() => {});

    // handling the navigation here so we can await the patch request before redirecting
    if (otherExperiencePath.startsWith('https')) {
      window.location.href = otherExperiencePath;
    } else {
      navigate(otherExperiencePath);
    }
  };

  return {
    id: 'team',
    icon: 'TeamSoccer' as const,
    path: defaultTeamPath,
    label: t('crossPlatform.primary.teams'),
    subNav: [
      ...teamSubnav,
      {
        id: 'team-switch-experience',
        label: (
          <div>
            <hr className="w-full absolute left-0 top-0 border-layering-1-strong" />
            <MenuItemText isTouch={isTouch}>
              <b>{t(`crossPlatform.secondary.${translationKey}`)}</b>
            </MenuItemText>
          </div>
        ),
        onClick: toggleTeamSectionXp,
      },
    ],
  };
};
